import { useMemo } from 'react';
import { useGetModuleByIdQuery } from '../../../../redux/services/module';
import useFormPermissions from '../../../../hooks/use-form-permissions';
import useProfileData from '../../../../hooks/use-profile-data';

/**
 * Custom hook that determines if the current user can manage global views based on form and module permissions.
 *
 * @param moduleId - The ID of the module to check permissions for
 * @param formId - The ID of the form to check permissions for
 * @param isAllForms - Whether to check permissions for all forms in the module or just the specific form
 *
 * @returns An object containing:
 * - `canManageGlobalViews`: Boolean indicating if the user has permission to manage global views
 * - `isLoading`: Boolean indicating if permission data is still being loaded
 *
 * @remarks
 * When `isAllForms` is true, the hook checks if the user belongs to any group that can manage global views across all forms in the module.
 * When `isAllForms` is false, it checks the specific form permissions for the current user.
 */
const useCanManageGlobalViews = (moduleId: number, formId: number, isAllForms: boolean) => {
  const { formPermissions, isLoading: isFormPermissionsLoading } = useFormPermissions(formId);

  const {
    data: {
      allFormConfigurationPermissionDTO: { groupsAbleToManageGlobalViews } = { groupsAbleToManageGlobalViews: [] },
    } = {},
    isLoading: isModuleLoading,
  } = useGetModuleByIdQuery({ moduleId }, { skip: !isAllForms });

  const { profileData: { user: { groups } = {} } = {} } = useProfileData();

  const canManageGlobalViewsOnAllForms = useMemo(() => {
    if (isAllForms && !isModuleLoading && groupsAbleToManageGlobalViews.length > 0 && groups && groups.length > 0) {
      const userGroups = groups.map((group) => group.id);

      return groupsAbleToManageGlobalViews.some((groupId) => userGroups.includes(groupId));
    }

    return false;
  }, [isModuleLoading, groupsAbleToManageGlobalViews, groups, isAllForms]);

  const canManageGlobalViews = useMemo(() => {
    if (isFormPermissionsLoading || isModuleLoading) {
      return false;
    }

    if (isAllForms) {
      return canManageGlobalViewsOnAllForms;
    } else {
      return Boolean(formPermissions?.canManageGlobalViews);
    }
  }, [isModuleLoading, isFormPermissionsLoading, formPermissions, isAllForms, canManageGlobalViewsOnAllForms]);

  return {
    canManageGlobalViews,
    isLoading: isFormPermissionsLoading || isModuleLoading,
  };
};

export default useCanManageGlobalViews;
